import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';

export class FormlyUtil {

  // ======================================================
  // PUBLIC API
  // ======================================================

  static cloneFormlyView(
    fields: FormlyFieldConfig[],
    model: any
  ): {
    form: FormGroup;
    fields: FormlyFieldConfig[];
    model: any;
  } {

    // 1️⃣ Clone everything (including runtime metadata)
    const clonedFields = this.deepClone(fields);
    const clonedModel = this.deepClone(model);

    // 2️⃣ Create new root form
    const form = new FormGroup({});

    // 3️⃣ Rebuild form controls safely
    this.rebindFormControls(clonedFields, form);

    // 4️⃣ Restore parent references
    this.rebindParents(clonedFields);

    return {
      form,
      fields: clonedFields,
      model: clonedModel,
    };
  }

  // ======================================================
  // DEEP CLONE (RUNTIME‑SAFE)
  // ======================================================

  static deepClone<T>(source: T): T {
    return this.cloneValue(source, new WeakMap());
  }

  private static cloneValue(value: any, visited: WeakMap<any, any>): any {

    // Primitive
    if (value === null || typeof value !== 'object') {
      return value;
    }

    // Circular reference
    if (visited.has(value)) {
      return visited.get(value);
    }

    // ✅ Keep Angular controls temporarily
    if (value instanceof AbstractControl) {
      return value;
    }

    // Date
    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    // Array
    if (Array.isArray(value)) {
      const arr: any[] = [];
      visited.set(value, arr);
      for (let i = 0; i < value.length; i++) {
        arr[i] = this.cloneValue(value[i], visited);
      }
      return arr;
    }

    // ✅ Preserve prototype
    const clone = Object.create(Object.getPrototypeOf(value));
    visited.set(value, clone);

    for (const key of Object.keys(value)) {
      clone[key] = this.cloneValue(value[key], visited);
    }

    return clone;
  }

  // ======================================================
  // FORM CONTROL REBINDING (READONLY SAFE)
  // ======================================================

  private static rebindFormControls(
    fields: FormlyFieldConfig[],
    form: FormGroup
  ): void {

    for (const field of fields) {

      // ✅ Leaf field with control
      if (field.formControl) {
        const old = field.formControl;

        const control = new FormControl(
          old.value,
          old.validator,
          old.asyncValidator
        );

        if (old.disabled) {
          control.disable({ emitEvent: false });
        }

        // ✅ Bypass readonly restriction SAFELY
        this.setReadonlyProperty(field, 'formControl', control);

        if (field.key != null) {
          form.addControl(field.key.toString(), control);
        }
      }

      // ✅ Field group
      if (field.fieldGroup?.length) {
        const group = new FormGroup({});

        if (field.key != null) {
          form.addControl(field.key.toString(), group);
        }

        this.rebindFormControls(field.fieldGroup, group);
      }
    }
  }

  // ======================================================
  // PARENT REBINDING
  // ======================================================

  private static rebindParents(
    fields: FormlyFieldConfig[],
    parent?: FormlyFieldConfig
  ): void {

    for (const field of fields) {
      this.setReadonlyProperty(field, 'parent', parent);

      if (field.fieldGroup?.length) {
        this.rebindParents(field.fieldGroup, field);
      }
    }
  }

  // ======================================================
  // READONLY PROPERTY OVERRIDE (FORMly‑SAFE)
  // ======================================================

  private static setReadonlyProperty<T extends object>(
    target: T,
    key: keyof T | string,
    value: any
  ): void {
    Object.defineProperty(target, key, {
      value,
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
}
